import { useContext, useState, ChangeEvent, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, Paper, Switch, Slider } from '@mui/material';
import { AccountCircle, Cookie, DisplaySettings, Edit, FingerprintOutlined, Logout } from '@mui/icons-material';

import { Popup } from '@/components';
import { User } from '@/tmp';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { checkCookiebot } from '@/utilities/checkCookiebot';
import { ctxUser } from '@/contexts/UserContext';
import { ctxPluto } from '@/contexts/PlutoContext';
import LanguageSelect from './LanguageSelect';
import { BiometicLoginSupport } from '@/types';
import TargetAttributesPopup from '@/components/targetAttributes/TargetAttributesPopup';
import { NewFeatureBadge } from '@/components/common/NewFeatureBadge';

import './SettingsPopup.css';

function openCookieManager() {
  Cookiebot.renew();
}

export default function SettingsPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useContext(ctxUser);
  const plutoCtx = useContext(ctxPluto);
  const [showTargetAttributes, setShowTargetAttributes] = useState(false);
  const [bio, setBio] = useState<BiometicLoginSupport>(() => {
    const biometric = localStorage.getItem('biometriclogin');
    if (biometric === null) return 'false';
    if (biometric === 'notsupported') return 'notsupported';
    if (biometric === 'off') return 'off';
    return biometric === 'true' ? 'true' : 'false';
  });
  const [sliderIndex, setSliderIndex] = useState(0);

  useEffect(() => {
    const pathLengthMinutes = Number(plutoCtx?.pathLengthMinutes);
    setSliderIndex(minuteOptions.indexOf(pathLengthMinutes));
  }, [plutoCtx?.pathLengthMinutes]);

  function handleBioChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    setBio(checked ? 'true' : 'off');
    localStorage.setItem('biometriclogin', checked ? 'true' : 'off');
  }

  function isNotTokenUser() {
    return !ctx?.isToken();
  }

  function isTargetAttributesEditorEnabled() {
    return ctx?.hasExtraFeature('extrafeature_pluto_targetattributes_tab');
  }

  if (!ctx) return;

  const title = t('mainMenu.settings');
  const user = ctx.user as User;

  const lengthOptionLabels = [
    t('general.disabledShort'),
    15 + t('general.dateTime.minute'),
    30 + t('general.dateTime.minute'),
    1 + t('general.dateTime.hour'),
    2 + t('general.dateTime.hour'),
    6 + t('general.dateTime.hour'),
  ];
  const minuteOptions = [0, 15, 30, 60, 120, 360];

  function handlePathLengthChange(_event: Event, newValue: number | number[]) {
    const index = newValue as number;
    setSliderIndex(index);
    const minutes = minuteOptions[index];
    plutoCtx?.setPathLengthMinutes(minutes);
  }

  const body = (
    <div>
      {isNotTokenUser() && (
        <div>
          <Typography variant="body2" className="settings-block-title" data-qa-element-id="profile_title_label">
            {t('settings.profile')}
          </Typography>
          <Paper className="settings-box" data-qa-element-id="profile_paper">
            <AccountCircle className="icon-left" />
            <Box className="box">
              <Typography data-qa-element-id="username_label">{user.name}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }} data-qa-element-id="email_label">
                {user.email}
              </Typography>
            </Box>
            <IconButton onClick={async () => await navigate('/logout')} data-qa-element-id="profle_logout_button">
              <Logout className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
      <div>
        <Typography variant="body2" className="settings-block-title" data-qa-element-id="language_title_label">
          {t('settings.language')}
        </Typography>
        <LanguageSelect />
      </div>
      {bio !== 'notsupported' && isNotTokenUser() && (
        <div>
          <Typography variant="body2" className="settings-block-title" data-qa-element-id="biometrics_title_label">
            {t('settings.biometrics')}
          </Typography>
          <Paper className="settings-box" data-qa-element-id="biometrics_paper">
            <FingerprintOutlined className="icon-left" />
            <Box className="box">
              <Typography data-qa-element-id="biometrics_label">{t('settings.biometricAuthentication')}</Typography>
            </Box>
            <Switch
              color="secondary"
              checked={bio !== 'off'}
              onChange={handleBioChange}
              data-qa-element-id="biometrics_swich_button"
            />
          </Paper>
        </div>
      )}
      {checkCookiebot() && isNotTokenUser() && (
        <div>
          <Typography variant="body2" className="settings-block-title" data-qa-element-id="cookies_title_label">
            {t('settings.cookies')}
          </Typography>
          <Paper className="settings-box" elevation={1} data-qa-element-id="cookies_paper">
            <Cookie className="icon-left" />
            <Box className="box">
              <Typography data-qa-element-id="cookies_label">{t('settings.cookieManagement')}</Typography>
            </Box>
            <IconButton onClick={openCookieManager} data-qa-element-id="open_cookies_button">
              <Edit className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
      {!isPhoneOrHighScreen() && isTargetAttributesEditorEnabled() && isNotTokenUser() && (
        <div>
          <Typography
            variant="body2"
            className="settings-block-title"
            data-qa-element-id="target_attributes_title_label"
          >
            {t('settings.targetAttributes')}
          </Typography>
          <Paper
            className={showTargetAttributes ? 'clickable settings-box settings-box-selected' : 'clickable settings-box'}
            data-qa-element-id="taregt_attributes_paper"
            onClick={() => setShowTargetAttributes(!showTargetAttributes)}
          >
            <NewFeatureBadge releaseDate="2026-04-21" top="0px" right="32px">
              <DisplaySettings className="icon-left" />
            </NewFeatureBadge>
            <Box className="box">
              <Typography data-qa-element-id="target_attributes_label">{t('settings.editTargetAttributes')}</Typography>
            </Box>
            <IconButton color="primary" data-qa-element-id="open_target_attributes_button">
              <Edit className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
      <div>
        <Typography className="settings-block-title">{t('settings.path')}</Typography>
        <Paper className="settings-box-column">
          <Box className="box">
            <Typography className="settings-text">{t('settings.pathLengthMinutes')}</Typography>
          </Box>
          <div style={{ padding: '0 15px' }}>
            <Slider
              min={0}
              max={minuteOptions.length - 1}
              step={1}
              marks={lengthOptionLabels.map((label, index) => ({
                value: index,
                label: label,
              }))}
              value={sliderIndex}
              onChange={handlePathLengthChange}
            />
          </div>
        </Paper>
      </div>
    </div>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <>
      <div className="settings-container" data-qa-element-id="settings_view">
        <div className="settings-header">
          <Typography variant="h6" data-qa-element-id="settings_view_title">
            {title}
          </Typography>
        </div>
        {body}
      </div>
      {showTargetAttributes && <TargetAttributesPopup closePopup={closePopup} />}
    </>
  );
}
